/**
 * add-ons.js — sections/add-ons.liquid only. Cart AJAX add-to-cart for the 4
 * standalone add-on products (each its own product, no variant toggle needed
 * — see DATABASE.md §1b/§4). Same fail-soft contract as packages.js: a plain
 * `<form action="/cart/add">` POST works with JS disabled/broken; this is a
 * progressive enhancement over that using `/cart/add.js`.
 *
 * Loaded as `<script type="module" defer>` — no globals, no dependencies.
 */

const LOG_PREFIX = "[BecomeFounder]";
const ADDED_LABEL_RESET_MS = 2000;

function initAddToCart() {
  if (typeof window.fetch !== "function") return;

  document.querySelectorAll("[data-addon-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      handleAddToCart(form).catch((err) => {
        console.warn(`${LOG_PREFIX} add-on add-to-cart handler failed`, err);
      });
    });
  });
}

async function handleAddToCart(form) {
  const button = form.querySelector("[data-add-to-cart]");
  const status = form.querySelector("[data-form-status]");
  const variantInput = form.querySelector('[name="id"]');
  const variantId = variantInput ? variantInput.value : "";

  if (!variantId) {
    setStatus(status, "This add-on isn't available yet — please check back soon.");
    return;
  }

  setLoading(button, true);
  setStatus(status, "");

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || "Could not add this add-on to your cart.");
    }

    setStatus(status, "Added to cart.");
    if (button) {
      button.dataset.originalHtml = button.dataset.originalHtml || button.innerHTML;
      button.textContent = "Added ✓";
      window.setTimeout(() => resetButton(button), ADDED_LABEL_RESET_MS);
    }
    // cart.js listens for this: it re-renders the drawer from the server and
    // opens it, so the shopper immediately sees what they now have and can
    // remove it. It also broadcasts `cart:count`, which updates this bar.
    document.dispatchEvent(new CustomEvent("cart:updated"));
  } catch (err) {
    console.warn(`${LOG_PREFIX} add-on add-to-cart failed`, err);
    setStatus(status, "Something went wrong — please try again.");
    resetButton(button);
  } finally {
    setLoading(button, false);
  }
}

/**
 * Keep the inline checkout bar's count in sync.
 *
 * This bar predates the cart drawer, when it was the ONLY route to checkout
 * for an add-on-only shopper (which is exactly how "EIN Only" is sold). The
 * drawer now covers that, and the bar's CTA opens the drawer rather than
 * jumping to checkout — but the bar stays, because an inline affordance right
 * under the grid is worth more than making someone hunt for the header icon.
 *
 * It no longer fetches /cart.js itself: cart.js broadcasts `cart:count` after
 * every mutation from anywhere on the page, so the bar stays honest about
 * removals made in the drawer too, at zero extra requests.
 */
function initCartBar() {
  document.addEventListener("cart:count", (event) => {
    const count = (event.detail && event.detail.count) || 0;
    document.querySelectorAll("[data-addons-cart-bar]").forEach((bar) => {
      const label = bar.querySelector("[data-cart-bar-count]");
      if (label) {
        const noun = count === 1 ? "item" : "items";
        label.innerHTML = count
          ? `<strong>${count} ${noun}</strong> in your cart.`
          : "";
      }
      bar.hidden = count === 0;
    });
  });
}

function setLoading(button, isLoading) {
  if (!button) return;
  if (isLoading) {
    button.dataset.originalHtml = button.dataset.originalHtml || button.innerHTML;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "Adding…";
  } else {
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }
}

function resetButton(button) {
  if (button && button.dataset.originalHtml) {
    button.innerHTML = button.dataset.originalHtml;
  }
}

function setStatus(status, message) {
  if (!status) return;
  status.textContent = message;
  status.classList.toggle("visually-hidden", message === "");
}

function init() {
  try {
    initAddToCart();
    initCartBar();
  } catch (err) {
    console.warn(`${LOG_PREFIX} add-ons.js init failed — continuing without enhancement`, err);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
